"use client";

import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-6 items-center">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-16 bg-muted animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-sans text-[14px] font-medium text-foreground transition-colors relative group py-2">
                {item.label}
                <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-secondary transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems
  } = useMenu();
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[100] bg-background flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-border/40">
        <Link to="/" onClick={onClose}>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1589716/img/emplify_world_logo.png" alt="Emplify.World" className="h-7" />
        </Link>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close menu">
          <X className="h-6 w-6 text-foreground" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto py-8 px-6 flex flex-col gap-6">
        {menuItems.map((item, i) => <div key={item.id} data-index={i}>
            <Link to={item.href || "#"} className="font-sans text-2xl font-medium text-foreground hover:text-secondary transition-colors block" onClick={onClose}>
              {item.label}
            </Link>
          </div>)}
        <div className="mt-auto pt-8 border-t border-border/40">
          <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-base" asChild>
            <Link to="/ContactPage" onClick={onClose}>Book Strategy Call</Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);
  return <>
      <header id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-[12px] border-b border-border shadow-sm py-3" : "bg-background border-b border-border/40 py-5"}`}>
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
          {}
          <Link to="/" className="flex-shrink-0 z-10">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1589716/img/emplify_world_logo.png" alt="Emplify.World" className="h-7 w-auto object-contain" />
          </Link>

          {}
          <div className="hidden md:flex flex-1 justify-center">
            <MenuProvider menu_id="43">
              <DesktopNav />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center gap-4 z-10">
            <Button className="hidden md:inline-flex bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105 transition-transform duration-300 shadow-sm" asChild>
              <Link to="/ContactPage">Book Strategy Call</Link>
            </Button>
            
            <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-accent" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu">
              <Menu className="h-6 w-6" />
            </Button>
          </div>
        </div>
      </header>

      {}
      <MenuProvider menu_id="43">
        <MobileNav isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </>;
}